import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/legacy/image';
import { Tab } from '@headlessui/react';
// TODO: dynamic import? react-google-maps/api is 125kb
import { LoadScriptNext } from '@react-google-maps/api';
import { Dialog, Theme, useMediaQuery, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import iconClose from '../public/images/close.svg';
import { Position } from '../types';
import { Building, StrapiMediaInfo } from '../apolloClient/types';
import { Listing } from '../apolloClient/types/Units';
import { loadScriptProps } from '../constants/gapi/googleMapsConfig';
import { usePopupTabs } from '../hooks/usePopupTabs';
import { useDialogExitTransition } from 'hooks/useDialogExitTransition';
import {
  pageScrollDialogSx,
  scrollOverlayToTop,
  usePageScrollOverlay,
} from 'hooks/usePageScrollOverlay';
import PopUpGallery from 'components/PopUpGallery';
import PopUpDirections from 'components/PopUpDirections';
import PopUpTour from 'components/PopUpTour';
import FloorplanPopupV2 from './BuldingV2/FloorplansV2/FloorplanPopupV2';
import RequestInfoBadgeV2 from './RequestInfoBadgeV2';

export type PopUpUnitBuildingV2Props = {
  images: StrapiMediaInfo[];
  open: boolean;
  onClose: () => void;
  renderHeader: (onClose: () => void) => React.JSX.Element;
  entity: Building | Listing;
  entityType: 'listing' | 'building';
  initialActiveRowIndex?: number;
  activeTab?: number;
  tabsRenderExclude?: {
    photos?: boolean;
    floorPlan?: boolean;
    directions?: boolean;
    tour?: boolean;
    sitePlan?: boolean;
  };
  position?: Position;
  tours?: string[] | string | null;
  toursButtonLabel?: string | null;
  floorplanButtonLabel?: string | null;
  initialSitePlan?: boolean;
  buildingFloorPlanImage?: Building['floorPlanImage'];
  openPopupLarge?: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      initialShowedIndex: number;
    }>
  >;
};

type PopupRenderProps = PopUpUnitBuildingV2Props & {
  onSelectFloorplanRow: (rowIndex: number) => void;
};

type PopupConfig = {
  name: string;
  title: string | ((props: PopUpUnitBuildingV2Props) => string);
  render: (
    props: PopupRenderProps,
    key: string,
    scrollRef?: React.RefObject<HTMLDivElement | null>
  ) => React.JSX.Element | string;
};

const popupConfig: PopupConfig[] = [
  {
    name: 'photos',
    title: 'Photos',
    render: ({ images, openPopupLarge }) => (
      <>
        <div className="pt-[20px] md:pt-[50px] md:overflow-y-auto md:h-full">
          <PopUpGallery images={images} openPopupLarge={openPopupLarge} />
        </div>
        <div className="md:hidden">
          <RequestInfoBadgeV2 isSticky={true} />
        </div>
      </>
    ),
  },
  {
    name: 'sitePlan',
    title: 'Site Plan',
    render: (
      {
        initialActiveRowIndex,
        initialSitePlan,
        entity,
        entityType,
        buildingFloorPlanImage,
        onSelectFloorplanRow,
      },
      key,
      scrollRef
    ) => (
      <div className="w-full max-w-[1368px] mx-auto lg:h-full lg:min-h-0 lg:overflow-hidden">
        <FloorplanPopupV2
          entity={entity}
          bodyType={entityType}
          initialActiveRowIndex={initialActiveRowIndex}
          initialSitePlan={true}
          showTable={entityType === 'building'}
          buildingFloorPlanImage={buildingFloorPlanImage}
          onSelectRow={onSelectFloorplanRow}
          scrollRef={scrollRef}
        />
      </div>
    ),
  },
  {
    name: 'floorPlan',
    title: ({ floorplanButtonLabel }) => floorplanButtonLabel || 'Floor plan',
    render: (
      {
        initialActiveRowIndex,
        initialSitePlan,
        entity,
        entityType,
        onSelectFloorplanRow,
      },
      key,
      scrollRef
    ) => (
      <div className="w-full max-w-[1368px] mx-auto lg:h-full lg:min-h-0 lg:overflow-hidden">
        <FloorplanPopupV2
          entity={entity}
          bodyType={entityType}
          initialActiveRowIndex={initialActiveRowIndex}
          initialSitePlan={false}
          scrollRef={scrollRef}
          onSelectRow={onSelectFloorplanRow}
        />
      </div>
    ),
  },
  {
    name: 'directions',
    title: 'Location',
    render: ({ position }, key) => (
      <div className="h-full overflow-y-auto container-1280">
        <PopUpDirections position={position} />
      </div>
    ),
  },
  {
    name: 'tour',
    title: ({ toursButtonLabel }) => toursButtonLabel || '',
    render: ({ tours }) => <PopUpTour tours={tours} />,
  },
];

// Photos and (below lg) plans keep the top gap as panel padding, so it scrolls away
function getScrollAreaMarginClass(tabName?: string): string {
  if (tabName === 'photos') return '';
  if (tabName === 'sitePlan' || tabName === 'floorPlan') return 'lg:mt-[50px]';
  return 'mt-[20px] md:mt-[50px]';
}

function getScrollAreaOverflowClass(
  isPageScroll: boolean,
  isFloorplanTab: boolean
): string {
  if (isPageScroll) return '';
  if (isFloorplanTab)
    return 'min-h-[0px] flex-1 overflow-auto no-scrollbar lg:overflow-hidden';
  return 'min-h-[0px] flex-1 overflow-auto no-scrollbar';
}

function getTabClassName(selected: boolean, isLast: boolean): string {
  const base = `w-full md:w-auto md:px-4 lg:px-10 md:h-[46px] outline-none flex md:items-center pt-[7px] md:pb-0 justify-center text-center border-gray-border border-b-gold border border-r-0 mb-0 relative ${
    isLast ? 'md:border-r' : ''
  }`;
  // Padding offsets the thicker underline so the label doesn't shift
  if (selected)
    return `${base} bg-gray-accent font-sohneKraftig border-b-black border-b-2 pb-[8px] md:pt-[1px]`;
  return `${base} bg-white pb-[9px] md:pt-0 hover-animation hover:bg-gray-accent`;
}

const PopupTab: React.FC<{ label: string; isLast: boolean }> = ({
  label,
  isLast,
}) => (
  <Tab className={({ selected }) => getTabClassName(selected, isLast)}>
    {({ selected }) => (
      <>
        <div
          className={
            selected
              ? 'absolute w-3 h-3 bg-gray-accent  bottom-[-0.455rem] bg-write  border-b-black border-b-2 border-r-2 transform rotate-45'
              : 'hidden'
          }
        />
        <span className={`!capitalize !text-13-17-0.3 truncate `}>{label}</span>
      </>
    )}
  </Tab>
);

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PopUpUnitBuildingV2: React.FC<PopUpUnitBuildingV2Props> = (props) => {
  const {
    activeTab,
    tabsRenderExclude = {},
    open,
    onClose,
    renderHeader,
  } = props;

  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const scrollRef = useRef<HTMLDivElement>(null);

  const { activeTabIndex, setActiveTabIndex, renderConfig } = usePopupTabs(
    popupConfig,
    activeTab,
    open,
    tabsRenderExclude as { [key: string]: boolean }
  );

  const [selectedFloorplanRow, setSelectedFloorplanRow] = useState(
    props.initialActiveRowIndex
  );

  useEffect(() => {
    if (open) {
      setSelectedFloorplanRow(props.initialActiveRowIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialActiveRowIndex, open]);

  const { isClosing, isEntered, requestClose, transitionProps } =
    useDialogExitTransition(onClose);

  // Phones scroll the window, not an inner div, so Safari can shrink its toolbar.
  // The slide animations run in the usual fixed layout.
  const isPageScroll = !isTablet && open && isEntered && !isClosing;
  usePageScrollOverlay(isPageScroll, scrollRef);

  const scrollToTopOnTabSwitch = useRef(false);

  // Safari cancels the row click's smooth scroll when the panel swaps, so redo it
  useEffect(() => {
    if (!scrollToTopOnTabSwitch.current) return;
    scrollToTopOnTabSwitch.current = false;
    if (scrollRef.current) scrollOverlayToTop(scrollRef.current);
  }, [activeTabIndex]);

  const handleSelectFloorplanRow = (rowIndex: number) => {
    setSelectedFloorplanRow(rowIndex);
    const floorPlanIndex = renderConfig.findIndex(
      (config) => config.name === 'floorPlan' && !config.skip
    );
    if (floorPlanIndex !== -1) {
      scrollToTopOnTabSwitch.current = floorPlanIndex !== activeTabIndex;
      setActiveTabIndex(floorPlanIndex);
    }
  };

  const renderProps: PopupRenderProps = {
    ...props,
    initialActiveRowIndex: selectedFloorplanRow,
    onSelectFloorplanRow: handleSelectFloorplanRow,
  };

  const activeTabName = renderConfig[activeTabIndex]?.name;
  const isFloorplanTab =
    activeTabName === 'sitePlan' || activeTabName === 'floorPlan';

  return (
    <LoadScriptNext {...loadScriptProps} loadingElement={undefined}>
      <Dialog
        open={open && !isClosing}
        onClose={requestClose}
        fullScreen
        disableScrollLock={!isTablet}
        TransitionComponent={Transition}
        TransitionProps={{ unmountOnExit: true, ...transitionProps }}
        sx={isPageScroll ? pageScrollDialogSx : undefined}
      >
        <div className="flex flex-col h-full">
          <Tab.Group
            className="flex flex-col flex-1 min-h-[0px]"
            selectedIndex={activeTabIndex}
            onChange={setActiveTabIndex}
          >
            <div className="sticky top-0 z-100 bg-white md:static md:z-auto">
              {renderHeader(requestClose)}
              <Tab.List className="outline-none">
                <div className="relative w-full max-w-[1368px] mx-auto flex flex-col items-center flex-1 z-100 bg-white md:pt-[30px]">
                  <div className="w-full flex flex-row flex-wrap items-center justify-between">
                    <div className="relative flex items-center w-full md:w-auto md:pl-[30px] lg:pl-[40px]">
                      {renderConfig.map(({ title, skip }, index) =>
                        skip ? (
                          <Tab key={index} />
                        ) : (
                          <PopupTab
                            key={index}
                            label={
                              typeof title === 'function' ? title(props) : title
                            }
                            isLast={renderConfig
                              .slice(index + 1)
                              .every((config) => config.skip)}
                          />
                        )
                      )}
                    </div>
                    <div className="hidden md:flex items-center md:pr-[30px] lg:pr-[40px]">
                      <RequestInfoBadgeV2
                        buttonClassName="!h-[46px]"
                        wrapperClassName="md:!mb-0"
                      />
                    </div>
                  </div>
                </div>
              </Tab.List>
            </div>
            <div
              ref={scrollRef}
              className={`scroll-smooth overscroll-none ${
                // Photos scroll inside the gallery, which keeps this gap as padding
                getScrollAreaMarginClass(activeTabName)
              } ${getScrollAreaOverflowClass(isPageScroll, isFloorplanTab)}`}
            >
              <Tab.Panels className="h-full">
                {renderConfig.map(({ render, skip, name }, index) => {
                  const isFloorplanPanel =
                    name === 'sitePlan' || name === 'floorPlan';
                  return skip ? (
                    <Tab.Panel className="outline-none" key={index} />
                  ) : (
                    <Tab.Panel
                      key={index}
                      className={`pb-2 ${
                        isFloorplanPanel
                          ? 'pt-[20px] md:pt-[50px] lg:pt-0 lg:h-full lg:min-h-0 lg:overflow-hidden'
                          : 'md:h-full md:min-h-0 md:overflow-hidden'
                      }`}
                    >
                      {render(renderProps, name, scrollRef)}
                    </Tab.Panel>
                  );
                })}
              </Tab.Panels>
            </div>
          </Tab.Group>
        </div>
      </Dialog>
    </LoadScriptNext>
  );
};

export default PopUpUnitBuildingV2;
